import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { blogService, BlogPost as BlogPostType } from '../services/blogService';
import { config } from '../config';
import SEO from '../components/common/SEO';
import { useUser } from '../contexts/UserContext';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
`;

const Breadcrumb = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: #999;

  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const CategoryBadge = styled.span`
  background-color: #007bff;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-block;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: white;
  margin: 0 0 1rem 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.95rem;
  color: #999;
  flex-wrap: wrap;
`;


const ViewCount = styled.span`
  &::before {
    content: '•';
    margin-right: 0.5rem;
  }
`;

const PublishedDate = styled.span`
  &::before {
    content: '•';
    margin-right: 0.5rem;
  }
`;

const AdminActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid #ffc107;
  border-radius: 8px;
`;

const AdminButton = styled.button<{ $variant?: 'danger' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  ${props => props.$variant === 'danger' ? `
    background-color: #dc3545;
    color: white;
    &:hover {
      background-color: #c82333;
    }
  ` : `
    background-color: #ffc107;
    color: #000;
    &:hover {
      background-color: #e0a800;
    }
  `}
`;

const ArticleContent = styled.article`
  color: #ddd;
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 3rem;

  h1 {
    color: white;
    font-size: 2rem;
    margin: 2rem 0 1rem 0;
    line-height: 1.3;
  }

  h2 {
    color: white;
    font-size: 1.75rem;
    margin: 1.75rem 0 1rem 0;
    line-height: 1.3;
  }

  h3 {
    color: white;
    font-size: 1.5rem;
    margin: 1.5rem 0 1rem 0;
    line-height: 1.3;
  }

  p {
    margin: 1rem 0;
  }

  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }

  li {
    margin: 0.5rem 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1.5rem 0;
  }

  blockquote {
    border-left: 4px solid #007bff;
    padding-left: 1rem;
    margin: 1.5rem 0;
    color: #aaa;
    font-style: italic;
  }

  code {
    background-color: rgb(31, 31, 31);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.9em;
  }

  pre {
    background-color: rgb(31, 31, 31);
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;

    code {
      background: none;
      padding: 0;
    }
  }
`;

const AuthorSection = styled.div`
  border-top: 2px solid #444;
  padding-top: 2rem;
  margin-top: 3rem;
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const AuthorImageLarge = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorLabel = styled.div`
  color: #999;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
`;

const AuthorName = styled(Link)`
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  text-decoration: none;
  display: block;
  margin-bottom: 0.5rem;

  &:hover {
    color: #007bff;
  }
`;

const AuthorBio = styled.p`
  color: #ccc;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #ff6b6b;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const BlogPost: React.FC = () => {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Note: Database stores as "admin" but TypeScript interface uses is_admin
  const isAdmin = (user?.user_permissions as any)?.admin || user?.user_permissions?.is_admin;

  useEffect(() => {
    if (id && slug) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, slug]);

  const fetchPost = async () => {
    if (!id || !slug) return;

    setLoading(true);
    setError(null);

    try {
      const data = await blogService.getPost(id, slug);
      setPost(data);
    } catch (err: any) {
      console.error('Error fetching post:', err);
      setError(err.message || 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleHidePost = async () => {
    if (!post || !isAdmin) return;

    const action = post.is_hidden ? 'unhide' : 'hide';
    if (!window.confirm(`Are you sure you want to ${action} this post?`)) {
      return;
    }

    try {
      await blogService.hidePost(post.id, !post.is_hidden);
      setPost({ ...post, is_hidden: !post.is_hidden });
    } catch (err: any) {
      alert(err.message || 'Failed to update post');
    }
  };

  const handleDeletePost = async () => {
    if (!post || !isAdmin) return;

    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      await blogService.deletePost(post.id);
      navigate('/blog');
    } catch (err: any) {
      alert(err.message || 'Failed to delete post');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading blog post...</LoadingMessage>
      </Container>
    );
  }

  if (error || !post) {
    return (
      <Container>
        <ErrorMessage>{error || 'Blog post not found'}</ErrorMessage>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title={`${post.title} - YouMeditation Blog`}
        description={post.excerpt || post.title}
        keywords={`${post.category_name || 'meditation'}, blog, ${post.title}`}
        url={`/blog/${post.id}/${post.slug}`}
        image={post.featured_image}
      />
      <Container>
        <Breadcrumb>
          <Link to="/">Home</Link>
          <span>›</span>
          <Link to="/blog">Blog</Link>
          <span>›</span>
          <span>{post.title}</span>
        </Breadcrumb>

        {isAdmin && (
          <AdminActions>
            <AdminButton onClick={handleHidePost}>
              {post.is_hidden ? 'Unhide Post' : 'Hide Post'}
            </AdminButton>
            <AdminButton $variant="danger" onClick={handleDeletePost}>
              Delete Post
            </AdminButton>
          </AdminActions>
        )}

        <Header>
          {post.category_name && (
            <CategoryBadge>{post.category_name}</CategoryBadge>
          )}
          <Title>{post.title}</Title>
          <Meta>
            {post.published_at && (
              <PublishedDate>{formatDate(post.published_at)}</PublishedDate>
            )}
            <ViewCount>{post.views.toLocaleString()} views</ViewCount>
          </Meta>
        </Header>

        <ArticleContent dangerouslySetInnerHTML={{ __html: post.content }} />

        {!post.is_anonymous && post.author_username && (
          <AuthorSection>
            {post.author_profile_image && (
              <AuthorImageLarge
                src={`https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${post.author_profile_image}`}
                alt={post.author_username}
              />
            )}
            <AuthorInfo>
              <AuthorLabel>Written by</AuthorLabel>
              <AuthorName to={`/profile/${post.author_username}`}>
                {post.author_username}
              </AuthorName>
              {post.author_about_me && (
                <AuthorBio>{post.author_about_me}</AuthorBio>
              )}
            </AuthorInfo>
          </AuthorSection>
        )}
      </Container>
    </>
  );
};

export default BlogPost;
